import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AddIcon,
  ConfirmDialog,
  DeleteIcon,
  DownloadTemplateIcon,
  EmptyState,
  ExportIcon,
  ScrollTable,
  UpdateFromFileIcon,
  UploadIcon,
  type ScrollTableColumn,
} from '@platform/ui-kit';
import { api, type AprApplication, type AprImportResult } from '../../api';
import { describeError, useAsyncData } from '../admin/useAsyncData';
import { ApplicationDetailDrawer } from './ApplicationDetailDrawer';
import { FilterBar } from './FilterBar';
import { LifecycleProgress } from './LifecycleProgress';
import { HelpPanel, type HelpContent } from './HelpPanel';
import { TOOLBAR } from './toolbarStyles';
import { ScreenHeader } from './ScreenHeader';
import { StatsStrip } from './StatsStrip';
import { APPLICATION_COLUMNS, APPLICATION_SAMPLES, MATCH_COLUMN } from './columns';
import {
  EMPTY_FILTERS,
  csvFromColumns,
  datedFilename,
  downloadFile,
  hasColumn,
  mapRow,
  hasActiveFilter,
  matchesFilter,
  matchesText,
  parseCsv,
  pickTextFile,
  type FilterValues,
} from './shared';

/**
 * Application Master Data.
 *
 * The foundational inventory: everything else — scoring, run rate, dispositions —
 * hangs off the applications registered here.
 *
 * Laid out as APR lays it out, so the screen reads the way the people using it
 * already expect: title with the actions on the right, a statistics strip, the filter
 * row, then the grid.
 *
 * Technology, Users, Owner and Type are on screen but not yet in the data. They are
 * kept as columns deliberately: the grid's shape is the specification, and adding
 * them later would silently change every saved column layout and CSV export.
 */

/**
 * The parent / child pills, exactly as APR draws them.
 *   parent  2px 8px · 11px · 600 · #1976d2 on white · radius 12
 *   child   2px 8px · 10px · 500 · #1565c0 on #e3f2fd · radius 12
 */
const PILL_BASE = {
  px: '8px',
  py: '2px',
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  lineHeight: 1.4,
  flexShrink: 0,
} as const;

const PARENT_PILL_SX = {
  ...PILL_BASE,
  fontSize: 11,
  fontWeight: 600,
  bgcolor: '#1976d2',
  // `&&` outranks the row-level colour rule above, so the pill stays white on blue.
  '&&': { color: '#fff' },
} as const;

const CHILD_PILL_SX = {
  ...PILL_BASE,
  fontSize: 10,
  fontWeight: 500,
  bgcolor: '#e3f2fd',
  '&&': { color: '#1565c0' },
} as const;

/** The same boxes as ACTION_BUTTON_SX, as plain CSS so a row costs no emotion work. */
const ACTION_BTN: React.CSSProperties = {
  padding: '4px 6px',
  borderRadius: '4px',
  border: '1px solid #1976d2',
  color: '#1976d2',
  background: 'transparent',
  fontSize: 14,
  lineHeight: 1,
  cursor: 'pointer',
};

const DANGER_BTN: React.CSSProperties = { ...ACTION_BTN, borderColor: '#d32f2f', color: '#d32f2f' };

/** One-line cell that keeps its full value on hover. APR shows N/A, not a blank. */
function Truncated({ value }: { value: string | null }) {
  // A bare <span>, not a Typography. The cell already carries APR's 13px/#333 from the
  // table rule, so a styled wrapper adds an emotion class and a DOM node per cell —
  // 5,790 of each on this screen. APR emits the text straight into the <td>.
  // It does not dim N/A either: every cell is #333, placeholder or not.
  return (
    <span title={value ?? ''} style={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
      {value || 'N/A'}
    </span>
  );
}

/**
 * Help copy, verbatim from APR.
 *
 * Kept as data so it can be rewritten as the applications are repositioned, without
 * touching the screen.
 */
const HELP: HelpContent = {
  icon: '📱',
  title: 'Application Master Data',
  purpose:
    'The foundational inventory of every application in your portfolio. Everything else — scoring, run rate, dispositions — hangs off the applications you register here.',
  doHere: [
    'Click Add Application to register a single app, or open the detail drawer on any row to edit it.',
    'Use Create from File to bulk-import new applications from a CSV, or Update from File to bulk-edit existing ones.',
    'Grab Download Template to get the correctly-formatted CSV, and Download Data to export the current inventory.',
    'Filter by text or vendor to narrow the list; parent applications group their child apps beneath them.',
    'Read the Progress column to see how far each application has moved through the lifecycle — hover any icon for its exact state.',
    'Delete All Data clears the entire inventory (use with care).',
  ],
  fits: 'Portfolio → Applications',
  firstTime:
    'Download the template, fill in your applications, then use Create from File to load them all at once.',
  sections: [
    {
      label: 'Key fields',
      definitions: [
        {
          term: 'Parent application',
          color: '#6d28d9',
          definition:
            'A roll-up record that groups related child apps; parents are excluded from additive portfolio totals.',
        },
        {
          term: 'Business criticality',
          color: '#dc2626',
          definition: 'How essential the app is to operations — feeds risk and prioritization.',
        },
        {
          term: 'Business function domain',
          color: '#2563eb',
          definition:
            'The business area the app supports, used to group and segment the portfolio.',
        },
      ],
    },
    {
      label: 'Progress column — the six lifecycle stages',
      paragraphs: [
        'Nothing in the Progress column is a stored flag. Every icon is read live from the record that owns that stage — the assessment, the disposition, the roadmap entry, the deployment log. There is no second copy to fall out of step.',
        'The six stages read grey for every application today: the modules that own them have not been migrated yet.',
      ],
    },
  ],
  links: [{ label: 'Current Run Rate', path: '/apr/portfolio/cost' }],
};

/**
 * The Add Application form, in the spec's order.
 *
 * Driven by the same column list as the template and the import, so the three cannot
 * drift: a field you can type here is a field you can upload, and vice versa.
 */
const ADD_FIELDS: {
  key: string;
  label: string;
  required?: boolean;
  multiline?: boolean;
  numeric?: boolean;
}[] = [
  { key: 'application_id_code', label: 'Application ID', required: true },
  { key: 'application_name', label: 'Application Name', required: true },
  { key: 'it_hardware_infrastructure', label: 'IT Hardware Infrastructure' },
  { key: 'date_of_introduction', label: 'Date of Introduction' },
  { key: 'contract_end_date', label: 'Contract End Date' },
  { key: 'application_owner', label: 'Application Owner' },
  { key: 'app_owner_email', label: 'App Owner Email' },
  { key: 'technical_owner_custodian', label: 'Technical Owner' },
  { key: 'to_email', label: 'Technical Owner Email' },
  { key: 'business_function_domain', label: 'Business Function / Domain' },
  { key: 'application_type', label: 'Application Type' },
  { key: 'technology_stack', label: 'Technology Stack' },
  { key: 'user_count', label: 'User Count', numeric: true },
  { key: 'business_criticality', label: 'Business Criticality' },
  { key: 'technical_health_status', label: 'Technical Health Status' },
  { key: 'integration_dependencies', label: 'Integration Dependencies' },
  { key: 'vendor_supplier', label: 'Vendor / Supplier' },
  { key: 'parent_application_id', label: 'Parent Application ID' },
  { key: 'description', label: 'Application Description', multiline: true },
  { key: 'internal_number', label: 'Internal Number' },
  { key: 'l1_business_functional_area', label: 'L1 - Business Functional Area' },
  { key: 'l2_capability_function', label: 'L2 - Capability Function' },
];

const NEW_APPLICATION: Record<string, string> = Object.fromEntries(
  ADD_FIELDS.map((f) => [f.key, '']),
);

/**
 * A compact scope toggle for the filter row.
 *
 * Deliberately small — a pill of about 120px, versus 207.5px for a text filter. The row
 * is not allowed to wrap once these are present, so anything wider would squeeze the
 * free-text box rather than sit beside the criticality filter as intended.
 *
 * It reads as a filter chip, not a form switch: these narrow the list exactly the way
 * the boxes to their left do, and a switch would suggest it changes the data.
 */
function ScopeToggle({
  label,
  title,
  on,
  onToggle,
}: {
  label: string;
  title: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <Tooltip title={title} placement="top">
      <Box
        component="button"
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={title}
        onClick={onToggle}
        sx={{
          flex: '0 0 auto',
          height: 33.24,
          px: 1.25,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          borderRadius: '4px',
          border: '1.25px solid',
          borderColor: on ? '#1976d2' : '#dddddd',
          borderWidth: on ? 2 : '1.25px',
          bgcolor: on ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
          color: on ? '#1976d2' : 'text.secondary',
          fontSize: 13,
          fontWeight: on ? 700 : 500,
          fontFamily: 'inherit',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: on ? '#1976d2' : '#bbbbbb' },
        }}
      >
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: on ? '#1976d2' : '#cbd5e1',
          }}
        />
        {label}
      </Box>
    </Tooltip>
  );
}

export function ApplicationMasterDataTab() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const applications = useAsyncData(() => api.apr.getApplications());
  const runRate = useAsyncData(() => api.apr.getRunRate());
  const session = useAsyncData(() => api.session());
  // Client Administrator, or any Talogent account — the same gate the server enforces.
  const isAdmin = Boolean(session.data?.user?.isAdmin || session.data?.user?.canAccessTalogent);

  const [text, setText] = useState('');
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  /**
   * Two SCOPE toggles, distinct from the text filters above.
   *
   *   licensing    -> license_negotiation, the applications carried into Licensing
   *                   Analysis for renegotiation.
   *   disposition  -> the applications eligible for a disposition decision, which is
   *                   NOT excluded_from_apr: that flag marks foundational platforms
   *                   that are auto-scored Retain and never assessed. So "eligible"
   *                   is the negation of the stored flag, not the flag itself.
   *
   * They are independent — turning both on asks for applications that are in scope for
   * both workstreams, not for either.
   */
  const [scopeLicensing, setScopeLicensing] = useState(false);
  const [scopeDisposition, setScopeDisposition] = useState(false);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState<AprImportResult>();
  const [actionError, setActionError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [adding, setAdding] = useState<Record<string, string>>();
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AprApplication>();

  // Memoized so the `?? []` fallback does not mint a new array each render and
  // invalidate every derived useMemo below it.
  const rows = useMemo(() => applications.data?.applications ?? [], [applications.data]);

  // The drawer's open record lives in the URL, so a deep link opens it cold and the
  // back button closes it — the same rule the shell's navigation follows.
  const selectedId = params.get('appId');
  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId || r.application_id_code === selectedId) ?? null,
    [rows, selectedId],
  );

  const openApplication = useCallback(
    (id: string | null, next: 'view' | 'edit' = 'view') => {
      setMode(next);
      setParams((current) => {
        const next = new URLSearchParams(current);
        if (id) next.set('appId', id);
        else next.delete('appId');
        return next;
      });
    },
    [setParams],
  );

  /** Annual run rate per application, for the drawer's cross-link. */
  const runRateByApplication = useMemo(() => {
    const totals = new Map<string, number>();
    for (const cost of runRate.data?.costs ?? []) {
      totals.set(
        cost.application_id,
        (totals.get(cost.application_id) ?? 0) + cost.total_annual_cost,
      );
    }
    return totals;
  }, [runRate.data]);

  const filtered = useMemo(() => {
    const matched = rows.filter(
      (row) =>
        // The eleven fields APR searches, in APR's order
        // (ApplicationMasterData.tsx:1341). Six of them were missing here, so a term
        // like "SAP" — which lives in vendor and technical-owner values — returned 25
        // rows against APR's 77.
        matchesText(text, [
          row.name,
          row.application_id_code,
          row.technology_stack,
          row.application_owner,
          row.application_type,
          row.it_hardware_infrastructure,
          row.vendor_supplier,
          row.technical_owner_custodian,
          row.business_criticality,
          row.technical_health_status,
          row.internal_number,
        ]) &&
        matchesFilter(filters.vendor, row.vendor_supplier) &&
        matchesFilter(filters.businessFunction, row.business_function_domain) &&
        matchesFilter(filters.applicationType, row.application_type) &&
        matchesFilter(filters.businessCriticality, row.business_criticality) &&
        (!scopeLicensing || row.license_negotiation === true) &&
        (!scopeDisposition || row.excluded_from_apr !== true),
    );

    // APR then EXPANDS the result: a parent that matched brings its whole family with
    // it, even children that did not match (ApplicationMasterData.tsx:1350). Without
    // this a matched parent renders as a roll-up over children that are not there.
    // Only the text filter expands — the dropdown filters do not, as in the source.
    if (!text.trim()) return matched;

    const matchedParentIds = new Set(matched.filter((a) => a.is_parent).map((a) => a.id));
    if (matchedParentIds.size === 0) return matched;

    const present = new Set(matched.map((a) => a.id));
    const extraChildren = rows.filter(
      (a) =>
        a.parent_application_id &&
        matchedParentIds.has(a.parent_application_id) &&
        !present.has(a.id),
    );
    return [...matched, ...extraChildren];
  }, [rows, text, filters, scopeLicensing, scopeDisposition]);

  // Children name their parent by id; the badge shows the code people recognise.
  const codeById = useMemo(() => new Map(rows.map((r) => [r.id, r.application_id_code])), [rows]);
  const parentCodeOf = (row: AprApplication) =>
    row.parent_application_id ? (codeById.get(row.parent_application_id) ?? null) : null;

  /**
   * The parent/child group the open record belongs to: the parent first, then its
   * children. Empty for a standalone application, which hides the family strip.
   */
  const family = useMemo(() => {
    if (!selected) return [];
    const parentId = selected.is_parent ? selected.id : selected.parent_application_id;
    if (!parentId) return [];
    const parent = rows.find((r) => r.id === parentId);
    const kids = rows.filter((r) => r.parent_application_id === parentId);
    return parent ? [parent, ...kids] : kids;
  }, [rows, selected]);

  const parents = useMemo(() => filtered.filter((a) => a.is_parent), [filtered]);
  const children = useMemo(() => filtered.filter((a) => !a.is_parent), [filtered]);

  /**
   * Each parent immediately followed by its own children, then everything else.
   *
   * The grid is a flat list — this ordering is what makes the family visible, and it
   * is why the rows are pre-sorted here rather than left to the column sort.
   */
  const ordered = useMemo(() => {
    const byParent = new Map<string, AprApplication[]>();
    for (const row of filtered) {
      if (!row.parent_application_id) continue;
      const group = byParent.get(row.parent_application_id);
      if (group) group.push(row);
      else byParent.set(row.parent_application_id, [row]);
    }

    const placed = new Set<string>();
    const out: AprApplication[] = [];

    for (const parent of filtered.filter((a) => a.is_parent)) {
      out.push(parent);
      placed.add(parent.id);
      for (const child of byParent.get(parent.id) ?? []) {
        out.push(child);
        placed.add(child.id);
      }
    }
    // Standalone applications, and any child whose parent the filter excluded.
    for (const row of filtered) if (!placed.has(row.id)) out.push(row);

    return out;
  }, [filtered]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(undefined);
    try {
      await fn();
      applications.reload();
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (importMode: 'create' | 'update') => {
    setBusy(true);
    setImportResult(undefined);
    setActionError(undefined);
    try {
      const csv = await pickTextFile();
      if (csv === null) return;

      const raw = parseCsv(csv);
      if (raw.length === 0) {
        setActionError('That file had no data rows. Start from the template.');
        return;
      }

      // An update file has to identify what it is updating.
      if (importMode === 'update' && !hasColumn(raw[0], MATCH_COLUMN)) {
        setActionError(
          'Update file must contain an Application ID or Application Code column to identify which applications to update.',
        );
        return;
      }

      const parsed = raw.map((row) => mapRow(row, APPLICATION_COLUMNS));

      setImportResult(
        importMode === 'create'
          ? await api.apr.bulkImportApplications(parsed)
          : await api.apr.bulkUpdateApplications(parsed),
      );
      applications.reload();
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // Widths are APR's percentages, verbatim from its <colgroup>
  // (ApplicationMasterData.tsx:2622) — 9/19/9/9/5/11/10/8/10/10, totalling 100.
  // The table is `table-layout: fixed`, so the colgroup alone sizes it.
  const columns: ScrollTableColumn<AprApplication>[] = [
    {
      key: 'application_id_code',
      label: 'Application ID',
      width: '9%',
      // APR puts the description in the cell's `title`, so hovering the ID shows it.
      cellProps: (row) => ({
        title: row.description || '',
        style: { cursor: row.description ? 'help' : 'default', position: 'relative' },
      }),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {row.application_id_code}
          <Tooltip title={row.description || 'No description recorded'}>
            <Box component="span" sx={{ ml: '4px', fontSize: 11, color: 'primary.main' }}>
              i
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: 'application_name',
      label: 'Application Name',
      width: '19%',
      // APR: flex row, 6px gap, name in <strong>, pill beside it.
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <Box component="strong" sx={{ fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.3 }}>
            {row.name}
          </Box>
          {row.is_parent ? (
            <Box sx={PARENT_PILL_SX}>
              Parent{row.child_count ? ` (${row.child_count} children)` : ''}
            </Box>
          ) : null}
          {row.parent_application_id ? (
            <Box sx={CHILD_PILL_SX}>Child of {parentCodeOf(row) ?? '—'}</Box>
          ) : null}
        </Box>
      ),
    },
    {
      key: 'progress',
      label: 'Progress',
      width: '9%',
      render: (row) => <LifecycleProgress application={row} />,
    },
    {
      key: 'technology_stack',
      label: 'Technology',
      width: '9%',
      render: (row) => <Truncated value={row.technology_stack} />,
    },
    {
      key: 'user_count',
      label: 'Users',
      width: '5%',
      align: 'right',
      render: (row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.user_count === null || row.user_count === undefined ? 'N/A' : String(row.user_count)}
        </span>
      ),
    },
    {
      key: 'it_hardware_infrastructure',
      label: 'IT Hardware / Infra',
      width: '11%',
      render: (row) => <Truncated value={row.it_hardware_infrastructure} />,
    },
    {
      key: 'application_owner',
      label: 'Owner',
      width: '10%',
      render: (row) => <Truncated value={row.application_owner} />,
    },
    {
      key: 'application_type',
      label: 'Type',
      width: '8%',
      render: (row) => <Truncated value={row.application_type} />,
    },
    {
      key: 'contract_end_date',
      label: 'Contract End Date',
      width: '10%',
      render: (row) => (
        <span style={row.contract_end_date ? undefined : { color: '#9e9e9e' }}>
          {row.contract_end_date || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '10%',
      // Stops a click on any button also opening the row's drawer.
      render: (row) => (
        // Plain buttons with a `title`, as APR has them. A Tooltip + IconButton + Box
        // per action is four MUI components x three actions x 579 rows — the single
        // biggest per-row cost on this screen, and it re-mounts on every keystroke in
        // the filter. APR renders three bare <button>s.
        <span
          style={{ display: 'flex', gap: 4, alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            title="View application"
            aria-label="View application"
            style={ACTION_BTN}
            onClick={() => openApplication(row.id, 'view')}
          >
            👁️
          </button>
          <button
            type="button"
            title="Edit application"
            aria-label="Edit application"
            style={ACTION_BTN}
            onClick={() => openApplication(row.id, 'edit')}
          >
            ✏️
          </button>
          <button
            type="button"
            title="Delete application"
            aria-label="Delete application"
            style={DANGER_BTN}
            onClick={() => setConfirmDelete(row)}
          >
            🗑️
          </button>
        </span>
      ),
    },
  ];

  const number = (value: number) => value.toLocaleString('en-US');

  return (
    <Box>
      <ScreenHeader
        title="Application Master Data"
        aside={<HelpPanel help={HELP} onNavigate={navigate} />}
        trail={[
          { label: 'Portfolio', path: '/apr/portfolio' },
          { label: 'Application Master Data' },
        ]}
        onNavigate={navigate}
        actions={
          <>
            <Button
              variant="contained"
              onClick={() => setAdding({ ...NEW_APPLICATION })}
              disabled={busy}
              sx={TOOLBAR.add}
              startIcon={<AddIcon />}
            >
              Add Application
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                downloadFile(
                  'application_update_template.csv',
                  csvFromColumns(APPLICATION_COLUMNS, APPLICATION_SAMPLES),
                )
              }
              sx={TOOLBAR.template}
              startIcon={<DownloadTemplateIcon />}
            >
              Download Template
            </Button>
            <Button
              variant="outlined"
              onClick={() => void upload('create')}
              disabled={busy}
              sx={TOOLBAR.create}
              startIcon={<UploadIcon />}
            >
              Create from File
            </Button>
            <Button
              variant="outlined"
              onClick={() => void upload('update')}
              disabled={busy}
              sx={TOOLBAR.update}
              startIcon={<UpdateFromFileIcon />}
            >
              Update from File
            </Button>
            {/* Administrator only, and hidden rather than disabled for everyone else —
                the server refuses it regardless. */}
            {isAdmin ? (
              <Button
                variant="contained"
                onClick={() => setConfirmWipe(true)}
                disabled={busy || rows.length === 0}
                sx={TOOLBAR.danger}
                startIcon={<DeleteIcon />}
              >
                Delete All Data
              </Button>
            ) : null}
            <Button
              variant="outlined"
              color="success"
              onClick={() =>
                downloadFile(
                  datedFilename('application_master_data'),
                  csvFromColumns(
                    APPLICATION_COLUMNS,
                    filtered as unknown as Record<string, unknown>[],
                  ),
                )
              }
              disabled={rows.length === 0}
              sx={TOOLBAR.download}
              startIcon={<ExportIcon />}
            >
              Download Data
            </Button>
          </>
        }
      />

      <StatsStrip
        stats={[
          {
            label: `${hasActiveFilter(text, filters) ? 'Filtered' : 'Total'} Applications`,
            value: number(children.length),
          },
          { label: 'Parent Applications', value: number(parents.length) },
          {
            label: 'Total Users',
            value: number(children.reduce((sum, a) => sum + (a.user_count ?? 0), 0)),
          },
          {
            label: 'Business Domains',
            value: number(
              new Set(children.map((a) => a.business_function_domain).filter(Boolean) as string[])
                .size,
            ),
          },
        ]}
      />

      <FilterBar
        searchPlaceholder="Filter applications by name, ID, technology, owner, type, infrastructure…"
        text={text}
        onTextChange={setText}
        filters={filters}
        onFiltersChange={setFilters}
        {...(hasActiveFilter(text, filters) || scopeLicensing || scopeDisposition
          ? { summary: `${filtered.length} of ${rows.length} shown` }
          : {})}
        extra={
          <>
            <ScopeToggle
              label="Licensing"
              title="Show only applications flagged as licence-negotiation candidates"
              on={scopeLicensing}
              onToggle={() => setScopeLicensing((v) => !v)}
            />
            <ScopeToggle
              label="Disposition"
              title="Show only applications eligible for a disposition decision — everything except foundational platforms marked not relevant for rationalization"
              on={scopeDisposition}
              onToggle={() => setScopeDisposition((v) => !v)}
            />
          </>
        }
      />

      <Stack spacing={2}>
        {applications.error ? <Alert severity="error">{applications.error}</Alert> : null}
        {actionError ? (
          <Alert severity="error" onClose={() => setActionError(undefined)}>
            {actionError}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity="success" onClose={() => setNotice(undefined)}>
            {notice}
          </Alert>
        ) : null}
        {importResult ? (
          // Rejected rows are listed rather than counted: "12 skipped" tells you
          // something is wrong, not which line to fix.
          <Alert
            severity={importResult.problems.length > 0 ? 'warning' : 'success'}
            onClose={() => setImportResult(undefined)}
          >
            <Typography variant="body2">
              {importResult.created} created · {importResult.updated} updated ·{' '}
              {importResult.skipped} skipped
            </Typography>
            {importResult.problems.length > 0 ? (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                {importResult.problems.slice(0, 10).map((problem) => (
                  <Typography component="li" variant="body2" key={problem}>
                    {problem}
                  </Typography>
                ))}
                {importResult.problems.length > 10 ? (
                  <Typography component="li" variant="body2" color="text.secondary">
                    …and {importResult.problems.length - 10} more.
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Alert>
        ) : null}

        {applications.loading ? (
          <Box sx={{ p: 6, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={
              hasActiveFilter(text, filters)
                ? 'No applications match this filter'
                : 'No applications yet'
            }
            description={
              hasActiveFilter(text, filters)
                ? 'Clear the filter to see the full inventory.'
                : 'Add one, or load a CSV with Create from File.'
            }
          />
        ) : (
          // Every row on one scrolling surface, exactly as APR has it — no pager, no
          // page size. See ScrollTable for why this is not the DataGrid.
          <ScrollTable<AprApplication>
            rows={ordered}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(row) => openApplication(row.id)}
            // Lets a deep link scroll straight to a row, as APR's ?appId effect does.
            getRowAttributes={(row) => ({ 'data-app-row': row.id })}
            // APR's row tinting, verbatim (ApplicationMasterData.tsx:2668): parents
            // anchor their family in blue and bold, children sit a shade back in grey,
            // and the row whose drawer is open goes amber with a 3px inset bar. Applied
            // inline, so a shaded row keeps its colour under the cursor.
            getRowStyle={(row) => ({
              ...(row.is_parent
                ? { backgroundColor: '#bbdefb', fontWeight: 600 }
                : row.parent_application_id
                  ? { backgroundColor: '#f5f5f5' }
                  : {}),
              ...(selected?.id === row.id
                ? { backgroundColor: '#fff3cd', boxShadow: 'inset 3px 0 0 #f59e0b' }
                : {}),
            })}
          />
        )}
      </Stack>

      <ApplicationDetailDrawer
        application={selected}
        mode={mode}
        onModeChange={setMode}
        onClose={() => openApplication(null)}
        onSaved={() => applications.reload()}
        runRateTotal={selected ? (runRateByApplication.get(selected.id) ?? null) : null}
        family={family}
        allApplications={rows}
        onSelect={(id) => openApplication(id)}
        onOpenRunRate={
          selected
            ? () => navigate(`/apr/portfolio/cost?appId=${encodeURIComponent(selected.id)}`)
            : undefined
        }
      />

      {/* Add Application — the few fields that are required to register one. The
          rest are filled in from the detail drawer once it exists. */}
      <Dialog open={Boolean(adding)} onClose={() => setAdding(undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Application</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mt: 1,
            }}
          >
            {ADD_FIELDS.map((field) => (
              <Box key={field.key} sx={field.multiline ? { gridColumn: '1 / -1' } : undefined}>
                <TextField
                  label={field.label}
                  size="small"
                  fullWidth
                  required={field.required ?? false}
                  multiline={field.multiline ?? false}
                  minRows={field.multiline ? 2 : undefined}
                  {...(field.numeric ? { type: 'number', inputProps: { min: 0 } } : {})}
                  value={adding?.[field.key] ?? ''}
                  onChange={(e) =>
                    setAdding((a) => (a ? { ...a, [field.key]: e.target.value } : a))
                  }
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdding(undefined)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.apr.createApplication(adding as Record<string, unknown>);
                setAdding(undefined);
                setNotice('Application added.');
              })
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete this application?"
        message={`${confirmDelete?.name ?? ''} will be retired. It stays in the audit trail and any cost record still points at it.`}
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={() =>
          void run(async () => {
            await api.apr.deleteApplication(confirmDelete!.id);
            setConfirmDelete(undefined);
            setNotice('Application deleted.');
          })
        }
        onCancel={() => setConfirmDelete(undefined)}
      />

      <ConfirmDialog
        open={confirmWipe}
        title="Delete all applications?"
        message={`All ${number(rows.length)} applications will be retired. They stay in the audit trail, but disappear from this screen and from every report.`}
        confirmLabel="Delete all"
        destructive
        busy={busy}
        onConfirm={() =>
          void run(async () => {
            const { deletedCount } = await api.apr.deleteAllApplications();
            setConfirmWipe(false);
            setNotice(
              `${number(deletedCount)} application${deletedCount === 1 ? '' : 's'} deleted.`,
            );
          })
        }
        onCancel={() => setConfirmWipe(false)}
      />
    </Box>
  );
}
